import { useCallback, useEffect, useRef } from 'react';
import { DateTime } from 'luxon';

/**
 * Custom React Hook: useStartOfMinuteInterval
 * 
 * This hook executes a provided callback function at the start of each minute.
 * The callback is triggered once at the start of the minute and then repeatedly
 * at exactly 1-minute intervals.
 *
 * Parameters:
 * - shouldStart (boolean): Determines whether the interval should start.
 * - callback (function): A function to be called on each minute. The callback 
 *   should accept a function `stop` as an argument to stop the interval or timeout.
 *
 * Example usage:
 * ```js
 * useStartOfMinuteInterval(true, (stop) => {
 *   console.log('Executed on minute');
 *   // Call stop() to clear the interval if needed
 * });
 * ```
 */
const useStartOfMinuteInterval = (
  shouldStart: boolean,
  callback: (stop: () => void) => void,
) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopTimeout = () => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }
  };
  
  const stopInterval = () => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
    }
  };

  const clearTimeoutAndInterval = useCallback(() => {
    stopInterval();
    stopTimeout();
  }, []);

  useEffect(() => {
    if (timeoutRef.current !== null || intervalRef.current !== null) return;

    if (shouldStart) {
      const now = DateTime.local();
      const msUntilNextMinute = (60 - now.second) * 1000;

      clearTimeoutAndInterval();

      timeoutRef.current = setTimeout(() => {
        callback(stop);
        intervalRef.current = setInterval(() => {
          callback(stop);
        }, 60000);
      }, msUntilNextMinute);
    }
  }, [callback, clearTimeoutAndInterval, shouldStart]);

  useEffect(() => {
    return clearTimeoutAndInterval;
  }, [clearTimeoutAndInterval]);
};

export default useStartOfMinuteInterval;
